'use client'

import { ReactNode } from 'react'
import { NavigationItem, ContextualAction, PrimaryAction } from './FourThreeOneNav'

// BIRHAUS: Utility function (replaces @/lib/utils cn)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

// BIRHAUS: Self-contained Button component for mobile
function MobileButton({
  children,
  variant = 'default',
  disabled = false,
  loading = false,
  onClick,
  className = ''
}: {
  children: ReactNode
  variant?: 'default' | 'secondary' | 'destructive' | 'primary'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  className?: string
}) {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
  
  const getVariantClass = (v: typeof variant): string => {
    switch (v) {
      case 'default': return "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-visible:ring-blue-500"
      case 'secondary': return "bg-gray-100 text-gray-900 hover:bg-gray-200 focus-visible:ring-gray-500"
      case 'destructive': return "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500"
      case 'primary': return "bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500"
      default: return "bg-white border border-gray-300 text-gray-900 hover:bg-gray-50 focus-visible:ring-blue-500"
    }
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(baseClasses, getVariantClass(variant), "h-10 px-4 py-2 text-sm rounded-md", className)}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
      ) : null}
      {children}
    </button>
  )
}

// BIRHAUS: Self-contained Badge component for mobile
function MobileBadge({
  children,
  variant = 'default',
  className = ''
}: {
  children: ReactNode
  variant?: 'default' | 'secondary' | 'destructive'
  className?: string
}) {
  const baseClasses = "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
  
  const getVariantClass = (v: typeof variant): string => {
    switch (v) {
      case 'default': return "bg-blue-100 text-blue-800"
      case 'secondary': return "bg-gray-100 text-gray-800"
      case 'destructive': return "bg-red-100 text-red-800"
      default: return "bg-blue-100 text-blue-800"
    }
  }

  return (
    <span className={cn(baseClasses, getVariantClass(variant), className)}>
      {children}
    </span>
  )
}

interface MobileNavigationProps {
  navigationItems: NavigationItem[]
  contextualActions: ContextualAction[]
  primaryAction?: PrimaryAction
  onItemClick: (item: NavigationItem) => void
  onCloseMobileMenu: () => void
  bp: string
}

export function MobileNavigation({
  navigationItems,
  contextualActions,
  primaryAction,
  onItemClick,
  onCloseMobileMenu,
  bp
}: MobileNavigationProps) {
  return (
    <div className={cn(`${bp}:hidden`, 'border-t border-gray-200 pt-4 pb-4')}>
      <div className="space-y-1">
        {navigationItems.map((item) => (
          <button
            key={item.id}
            onClick={() => onItemClick(item)}
            disabled={item.disabled}
            className={cn(
              "w-full text-left flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
              item.active 
                ? "text-blue-700 bg-blue-50"
                : "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
              item.disabled && "opacity-50 cursor-not-allowed"
            )}
            aria-current={item.active ? 'page' : undefined}
          >
            {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <MobileBadge variant="default">
                {item.badge}
              </MobileBadge>
            )}
          </button>
        ))}
      </div>
      
      {/* Mobile Actions */}
      {(contextualActions.length > 0 || primaryAction) && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          {contextualActions.map((action) => (
            <MobileButton
              key={action.id}
              onClick={() => {
                action.onClick()
                onCloseMobileMenu()
              }}
              variant={action.variant === 'destructive' ? 'destructive' : 'secondary'}
              disabled={action.disabled}
              loading={action.loading}
              className="w-full justify-center"
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </MobileButton>
          ))}
          
          {primaryAction && (
            <MobileButton
              onClick={() => {
                primaryAction.onClick()
                onCloseMobileMenu()
              }}
              variant="primary"
              disabled={primaryAction.disabled}
              loading={primaryAction.loading}
              className="w-full justify-center"
            >
              {primaryAction.icon && <span className="mr-2">{primaryAction.icon}</span>}
              {primaryAction.label}
            </MobileButton>
          )}
        </div>
      )}
    </div>
  )
}
